import React, { memo, useCallback, useEffect, useState, Fragment } from 'react';
import cn from 'classnames';
import { BodyCellPure, BodyWrapper } from '@creditflowresearch/uilib/Table/DataTable';
import { IColumn } from '@creditflowresearch/uilib/Table/DataTable/interfaces';

import styles from './expandableRow.module.scss';

interface IExpandableRowProps {
  index: number,
  row: Record<string, any>,
  columns: IColumn[],
  columnsLength: number,
  expandableRowContent: React.ElementType,
  isExpandedExternal: boolean,
  expandCollapseOnRowClick: boolean,
  isNonExpandable?: boolean,
  isDefaultExpanded?: boolean,
  forceRowWrapper: boolean,
  onRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement>,
    params: { row: Record<string, any>, index: number, isExpanded: boolean },
  ) => void,
  RowWrapper: React.ElementType,
  rowClassNameCallback?: (rowData: Record<string, any>) => string,
}

const ExpandableRow = ({
  index,
  row,
  columns,
  columnsLength,
  expandableRowContent: ExpandableRowContent,
  isExpandedExternal,
  expandCollapseOnRowClick,
  forceRowWrapper,
  RowWrapper,
  isNonExpandable = false,
  isDefaultExpanded = false,
  onRowClick = null,
  rowClassNameCallback,
}: IExpandableRowProps) => {
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded);

  useEffect(() => {
    if (isNonExpandable && !isDefaultExpanded) {
      setIsExpanded(false);
      return;
    }

    if (isDefaultExpanded && !isExpandedExternal) {
      setIsExpanded(true);
      return;
    }

    setIsExpanded(isExpandedExternal);
  }, [isExpandedExternal, isNonExpandable, isDefaultExpanded]);

  const handleRowClick = useCallback((e: React.MouseEvent<HTMLTableRowElement>) => {
    if (onRowClick) {
      onRowClick(e, { row, index, isExpanded });
    }

    if (!expandCollapseOnRowClick && !isDefaultExpanded) return;
    if (isNonExpandable) return;

    setIsExpanded((prev) => !prev);
  }, [
    row,
    index,
    isExpanded,
    onRowClick,
    expandCollapseOnRowClick,
    isNonExpandable,
    isDefaultExpanded,
  ]);

  const renderCell = useCallback((columnData: IColumn) => (
    <BodyCellPure
      cellCallback={ columnData.cellCallback }
      className={ columnData.className }
      key={ columnData.name }
      name={ columnData.name }
      title={ columnData.title }
      rowIndex={ index }
      rowData={ row }
      isVisible={ columnData.isVisible }
      width={ columnData.width }
      minWidth={ columnData.minWidth }
      isExpanded={ isExpanded }
    />
  ), [isExpanded, index, row]);

  const renderTableContent = () => (
    <table>
      <BodyWrapper>
        <tr
          className={ cn(
            rowClassNameCallback?.(row),
            {
              isRowExpanded: isExpanded,
              [styles.expandableRowHeader]: expandCollapseOnRowClick || isDefaultExpanded || !!onRowClick,
            },
          ) }
          aria-expanded={ isExpanded }
          onClick={ handleRowClick }
        >
          { columns.map(renderCell) }
        </tr>
        { isExpanded && (
          <tr>
            <td
              colSpan={ columnsLength }
              className={ styles.expandableContentCell }
            >
              <ExpandableRowContent rowData={ row } />
            </td>
          </tr>
        ) }
      </BodyWrapper>
    </table>
  );

  return (
    <tr className={ styles.expandableRow }>
      <td className={ styles.expandableCell } colSpan={ columnsLength }>
        { (isDefaultExpanded && !forceRowWrapper) ? (
          renderTableContent()
        ) : (
          <RowWrapper { ...(RowWrapper !== Fragment ? { row } : {}) }>
            { renderTableContent() }
          </RowWrapper>
        ) }
      </td>
    </tr>
  );
};

export default memo(ExpandableRow);
